import type {
    Combination,
    ComponentCombinations,
    ComponentRates,
    PanelDrilldown,
    QueryBudgetEstimate,
    Stats,
} from "../types/api";
import { reportNetworkFailure, reportNetworkSuccess } from "./connectionStatus";

const API_BASE = "/v1";

// Cloud Run aborts with 500 "no available instance" when capacity is exhausted
// (scale-from-zero or all instances at concurrency). Prefer waiting + retry so
// the dashboard loads fully rather than rendering a half-baked error state.
const RETRYABLE_STATUS = new Set([500, 502, 503, 504]);
const MAX_ATTEMPTS = 6;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 12000;

/** Delay before retry after failure index 0.. (exported for tests). */
export function retryBackoffMs(retryIndex: number): number {
    const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** retryIndex);
    const jitter = Math.random() * 0.3 * exp;
    return Math.round(exp + jitter);
}

export function isRetryableHttpStatus(status: number): boolean {
    return RETRYABLE_STATUS.has(status);
}

export interface ApiErrorDetail {
    code?: string;
    message?: string;
    allowed?: boolean;
    estimated_bytes?: number;
    limit_bytes?: number;
    file_count?: number;
    countries?: string[];
    period_start?: string;
    period_end?: string;
    unavailable_countries?: string[];
    requested_countries?: string[];
}

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly detail: string | ApiErrorDetail
    ) {
        super(typeof detail === "string" ? detail : (detail.message ?? `API error: ${status}`));
    }
}

export function queryBudgetEstimateFromError(error: Error | null): QueryBudgetEstimate | null {
    if (!(error instanceof ApiError) || typeof error.detail === "string") return null;
    const detail = error.detail;
    if (
        detail.code !== "query_budget_exceeded" ||
        typeof detail.estimated_bytes !== "number" ||
        typeof detail.limit_bytes !== "number" ||
        typeof detail.file_count !== "number" ||
        !Array.isArray(detail.countries) ||
        typeof detail.period_start !== "string" ||
        typeof detail.period_end !== "string"
    ) {
        return null;
    }
    return {
        allowed: false,
        estimated_bytes: detail.estimated_bytes,
        limit_bytes: detail.limit_bytes,
        file_count: detail.file_count,
        countries: detail.countries,
        period_start: detail.period_start,
        period_end: detail.period_end,
    };
}

export interface CoverageIncomplete {
    unavailable: string[];
    requested: string[];
}

/**
 * Extract the structured detail of a `stats_coverage_incomplete` 422, which
 * names the selected countries that have no data for the requested period.
 * Returns null for any other error so callers can fall back to generic handling.
 */
export function coverageIncompleteFromError(error: Error | null): CoverageIncomplete | null {
    if (!(error instanceof ApiError) || typeof error.detail === "string") return null;
    const detail = error.detail;
    if (
        detail.code !== "stats_coverage_incomplete" ||
        !Array.isArray(detail.unavailable_countries) ||
        detail.unavailable_countries.length === 0
    ) {
        return null;
    }
    return {
        unavailable: detail.unavailable_countries,
        requested: Array.isArray(detail.requested_countries) ? detail.requested_countries : [],
    };
}

async function fetchApi<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    const url = new URL(endpoint, window.location.origin);

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.set(key, String(value));
            }
        });
    }

    let lastError: Error | undefined;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (attempt > 0) {
            await new Promise((r) => setTimeout(r, retryBackoffMs(attempt - 1)));
        }

        let response: Response;
        try {
            response = await fetch(url.toString());
        } catch (err) {
            // Network error / connection reset — retry
            lastError = err instanceof Error ? err : new Error(String(err));
            continue;
        }

        if (isRetryableHttpStatus(response.status)) {
            // Prefer server text when present (e.g. "no available instance").
            const body = await response.text().catch(() => "");
            let detail = `API error: ${response.status}`;
            try {
                const j = JSON.parse(body) as { detail?: string };
                if (j.detail) detail = j.detail;
            } catch {
                if (body.trim()) detail = body.slice(0, 200);
            }
            lastError = new Error(detail);
            continue;
        }

        if (!response.ok) {
            if (response.status === 401) {
                window.location.href = "/auth/login";
                return new Promise(() => {});
            }
            const error = await response.json().catch(() => ({ detail: response.statusText }));
            throw new ApiError(response.status, error.detail || `API error: ${response.status}`);
        }

        reportNetworkSuccess();
        return response.json();
    }

    reportNetworkFailure();
    throw lastError ?? new Error("Request failed after retries");
}

export interface CombinationsParams {
    anchor: string;
    country: string;
    sub_market?: string;
    sales_channels?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    door_solution?: string;
    door_color?: string;
    has_interior?: boolean;
    has_lighting?: boolean;
    has_doors?: boolean;
    has_knobs_handles?: boolean;
    has_wall_mounting?: boolean;
    // Cross-filter params
    color?: string;
    height_cm?: number | string;
    depth_cm?: number;
    width_cm?: number | string;
    // Component family filters
    door_family?: string;
    interior_family?: string;
    lighting_family?: string;
    frame_types?: string;
    sort_by?: string;
}

export async function fetchCombinations(params: CombinationsParams): Promise<Combination[]> {
    return fetchApi<Combination[]>(`${API_BASE}/combinations`, { ...params });
}

export interface StatsParams {
    anchor: string;
    country: string;
    sub_market?: string;
    sales_channels?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    // Cross-filter params
    color?: string;
    height_cm?: number | string;
    depth_cm?: number | string;
    width_cm?: number | string;
    door_solution?: string;
    door_color?: string;
    frame_types?: string;
    has_doors?: boolean;
    has_interior?: boolean;
    has_lighting?: boolean;
    has_knobs_handles?: boolean;
    has_wall_mounting?: boolean;
    // Component family filters
    door_family?: string;
    interior_family?: string;
    lighting_family?: string;
    knobs_family?: string;
    loose_interior_family?: string;
    legs_family?: string;
    top_panels_family?: string;
    // Component product type filters
    interior_product_type?: string;
    knobs_product_type?: string;
    lighting_product_type?: string;
    loose_interior_product_type?: string;
    legs_product_type?: string;
    top_panels_product_type?: string;
    // Component color filters
    interior_color?: string;
    knobs_color?: string;
    loose_interior_color?: string;
    legs_color?: string;
    top_panels_color?: string;
    lighting_color?: string;
    // Component item filters
    interior_item?: string;
    door_item?: string;
    knobs_item?: string;
    lighting_item?: string;
    loose_interior_item?: string;
    legs_item?: string;
    top_panels_item?: string;
}

export async function fetchStats(params: StatsParams): Promise<Stats> {
    return fetchApi<Stats>(`${API_BASE}/stats`, { ...params });
}

export type QueryEstimateParams = Pick<StatsParams, "anchor" | "country" | "period" | "start_date" | "end_date">;

export async function fetchQueryEstimate(params: QueryEstimateParams): Promise<QueryBudgetEstimate> {
    return fetchApi<QueryBudgetEstimate>(`${API_BASE}/query-estimate`, { ...params });
}

export async function fetchRates(params: StatsParams): Promise<ComponentRates> {
    return fetchApi<ComponentRates>(`${API_BASE}/rates`, { ...params });
}

export interface ComponentCombinationsParams {
    anchor: string;
    country: string;
    component_type: "fronts" | "doors" | "interior" | "loose_interior" | "lighting" | "knobs_handles" | "legs" | "top_panels" | "eket" | "drawer_fronts";
    sub_market?: string;
    sales_channels?: string;
    period?: string;
    // Server-side pagination params
    generic_page?: number;
    generic_page_size?: number;
    with_qty_page?: number;
    with_qty_page_size?: number;
    generic_filter?: string;
    // Cross-filter params
    color?: string;
    height_cm?: number | string;
    depth_cm?: number | string;
    width_cm?: number;
    component_color?: string;
    front_types?: string;
    runner_types?: string;
    frame_types?: string;
    component_family?: string;
    door_family?: string;
    door_item?: string;
    door_color?: string;
    door_solution?: string;
    knobs_family?: string;
    knobs_item?: string;
    knobs_color?: string;
    component_product_type?: string;
    component_item?: string;
    top_panel_size?: string;
    top_panel_kind?: string;
    // Cross-tab drill-down (same fields as /v1/rates; own-tab keys omitted client-side)
    interior_family?: string;
    lighting_family?: string;
    interior_product_type?: string;
    interior_color?: string;
    interior_item?: string;
    knobs_product_type?: string;
    lighting_product_type?: string;
    lighting_color?: string;
    lighting_item?: string;
    loose_interior_family?: string;
    loose_interior_product_type?: string;
    loose_interior_color?: string;
    loose_interior_item?: string;
    legs_family?: string;
    legs_product_type?: string;
    legs_color?: string;
    legs_item?: string;
    top_panels_family?: string;
    top_panels_product_type?: string;
    top_panels_color?: string;
    top_panels_item?: string;
}

export async function fetchComponentCombinations(
    params: ComponentCombinationsParams
): Promise<ComponentCombinations> {
    return fetchApi<ComponentCombinations>(`${API_BASE}/component-combinations`, { ...params });
}

export interface SlidingDoorPanelsParams {
    anchor: string;
    country: string;
    frame_width_cm: number;
    frame_height_cm: number;
    frame_color: string;
    bu_code?: string;
    sales_channels?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    color?: string;
    height_cm?: number | string;
    depth_cm?: number | string;
    width_cm?: number | string;
    door_solution?: string;
    door_color?: string;
    frame_types?: string;
    sku_page?: number;
    sku_page_size?: number;
}

export async function fetchSlidingDoorPanels(
    params: SlidingDoorPanelsParams
): Promise<PanelDrilldown> {
    return fetchApi<PanelDrilldown>(`${API_BASE}/sliding-door-panels`, { ...params });
}

export async function fetchAnchors(): Promise<string[]> {
    return fetchApi<string[]>(`${API_BASE}/anchors`);
}

export interface AnchorConfig {
    anchor: string;
    feature_flags: string[];
    frame_type_categories: string[];
    /** Default frame types when no explicit filter is set, or null (= all) */
    default_frame_types: string[] | null;
    /** Earliest date with data (ISO format), or null if unrestricted */
    data_available_from: string | null;
    /** Configured companion families per component type, e.g. loose_interior. */
    component_families?: Record<string, ComponentFamily[]>;
}

export interface ComponentFamily {
    name: string;
    /** Countries where the family counts as a companion; null = all. */
    countries: string[] | null;
}

export async function fetchAnchorConfig(anchor: string): Promise<AnchorConfig> {
    return fetchApi<AnchorConfig>(`${API_BASE}/anchor-config`, { anchor });
}

export async function fetchCountries(anchor?: string): Promise<string[]> {
    return fetchApi<string[]>(`${API_BASE}/countries`, anchor ? { anchor } : {});
}

export interface SubMarket {
    code: string;
    name: string | null;
}

export async function fetchSubMarkets(country: string): Promise<SubMarket[]> {
    return fetchApi<SubMarket[]>(`${API_BASE}/sub-markets`, { country });
}

export interface FilterOptions {
    colors: string[];
    widths: number[];
    total_widths: number[];
    heights: number[];
}

export interface FilterOptionsParams {
    anchor: string;
    country: string;
    sub_market?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    frame_types?: string;
    sales_channels?: string;
}

export async function fetchFilterOptions(params: FilterOptionsParams): Promise<FilterOptions> {
    return fetchApi<FilterOptions>(`${API_BASE}/filter-options`, { ...params });
}

export interface FilterCounts {
    countries: Record<string, number>;
    sub_markets: Record<string, number>;
    channels: Record<string, number>;
}

export interface FilterCountsParams {
    anchor: string;
    country?: string;
    sub_market?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    frame_types?: string;
    sales_channels?: string;
}

export async function fetchFilterCounts(params: FilterCountsParams): Promise<FilterCounts> {
    return fetchApi<FilterCounts>(`${API_BASE}/filter-counts`, { ...params });
}

// Country Summary types
export interface CountrySummaryRow {
    country: string;
    currency: string;
    frames_doors_value_local: number | null;
    frames_doors_interior_value_local: number | null;
    frames_doors_others_value_local: number | null;
    frames_doors_value_eur: number | null;
    frames_doors_interior_value_eur: number | null;
    frames_doors_others_value_eur: number | null;
}

export interface CountrySummary {
    anchor: string;
    period_start: string;
    period_end: string;
    rows: CountrySummaryRow[];
    total_frames_doors_value_eur: number | null;
    total_frames_doors_interior_value_eur: number | null;
    total_frames_doors_others_value_eur: number | null;
    total_frames_doors_value_local: number | null;
    total_frames_doors_interior_value_local: number | null;
    total_frames_doors_others_value_local: number | null;
    total_currency: string | null;
}

export interface CountrySummaryParams {
    anchor: string;
    country: string;
    sub_market?: string;
    sales_channels?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
}

export async function fetchCountrySummary(params: CountrySummaryParams): Promise<CountrySummary> {
    return fetchApi<CountrySummary>(`${API_BASE}/country-summary`, { ...params });
}

/** Authenticated user, as returned by GET /auth/me. */
export interface CurrentUser {
    sub: string;
    display_name: string;
    mail: string;
    office_location: string | null;
    department: string | null;
    job_title: string | null;
    /** Business unit location, e.g. "STO.445". */
    location: string | null;
    country: string | null;
}

/**
 * Fetch the current user, throwing on HTTP/network failure.
 *
 * Unlike {@link fetchCurrentUser}, this does NOT collapse failures into
 * `null`: a non-OK response, a network error, or a timeout rejects. This lets
 * callers distinguish "the user genuinely has no country" (resolves to a
 * CurrentUser with `country: null`) from "the request failed" (rejects) —
 * important when the outcome is cached, so a transient failure isn't stored as
 * a definitive answer.
 *
 * A timeout guarantees the promise always settles, so callers that gate UI on
 * its completion (e.g. the home-country resolution guards) can't hang forever
 * on an unresponsive backend.
 */
export async function fetchCurrentUserOrThrow(timeoutMs = 5000): Promise<CurrentUser> {
    const res = await fetch("/auth/me", {
        credentials: "same-origin",
        signal: AbortSignal.timeout(timeoutMs),
    });
    if (!res.ok) {
        throw new Error(`/auth/me failed with status ${res.status}`);
    }
    return (await res.json()) as CurrentUser;
}

/**
 * Fetch the current user for analytics context. Returns null instead of
 * throwing or redirecting when unauthenticated, so analytics stays best-effort.
 */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
    try {
        return await fetchCurrentUserOrThrow();
    } catch {
        return null;
    }
}

// Admin: Cloud Run scaling removed — replaced by frontend keep-warm heartbeat
